import React from 'react';
import cx from 'classnames';
import Lottie from 'react-lottie';
import { BarChart, Bar, XAxis, ReferenceLine, LabelList } from 'recharts';
import graphicAnimation from './grafico.json';
import styles from './style.module.css';

interface Props {
  userValue: number;
  ready?: boolean;
  loading?: boolean;
  prices: {
    averagePrice: number;
    priceFipe: number;
  };
}

export const PriceComparison = ({ userValue = 0, ready, prices, loading }: Props) => {
  const renderAnimation = () => {
    const animationOptions = {
      loop: true,
      autoplay: true,
      animationData: graphicAnimation,
      renderSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    };
    const transitionAnimation = cx('ml16', styles.transitionAnimation);

    return (
      <div className={transitionAnimation}>
        <Lottie
          options={animationOptions}
          height={130}
          width={292}
          isStopped={ready}
          isPaused={ready}
          isClickToPauseDisabled
        />
      </div>
    );
  };

  const calculateUserReferenceBar = () => {
    const { averagePrice, priceFipe } = prices;
    const higherValue = Math.max(averagePrice, priceFipe);
    return userValue >= higherValue;
  };

  const renderLabelValue = (props: any) => {
    // eslint-disable-next-line react/prop-types
    const { x, width, value } = props;
    return (
      <text
        x={x + width / 2}
        y={130}
        style={{ fill: '#54575f', fontWeight: 'bold', fontSize: 12 }}
        textAnchor="middle"
        dominantBaseline="bottom"
      >
        {value}
      </text>
    );
  };

  const renderLabelTitle = (props: any) => {
    // eslint-disable-next-line react/prop-types
    const { x, width, value } = props;
    return (
      <text
        x={x + width / 2}
        y={145}
        style={{ fill: '#54575f', fontWeight: 300, fontSize: 12 }}
        textAnchor="middle"
        dominantBaseline="bottom"
      >
        {value}
      </text>
    );
  };

  const handleTitle = (data: any) => {
    return (
      <text x={20} y={data.viewBox.y} style={{ fontSize: 12, fontWeight: 400, fill: '#54575f' }}>
        Seu anúncio
      </text>
    );
  };
  const formatPrice = (price: number) => {
    return (
      'R$ ' +
      price
        .toFixed(2)
        .replace('.', ',')
        .replace(/(\d)(?=(\d{3})+\,)/g, '$1.')
    );
  };
  const handleText = (data: any) => {
    return (
      <text
        x={20}
        y={data.viewBox.y + 15}
        style={{ fontSize: 12, fontWeight: 700, fill: '#54575f' }}
      >
        {formatPrice(userValue)}
      </text>
    );
  };

  const renderChart = () => {
    const { priceFipe, averagePrice } = prices;
    const data = [
      {
        name: '',
        userValue,
        fipeValue: priceFipe,
        firstLabel: 'Média Autoscar',
        secondLabel: 'Tabela FIPE',
        autoscarValue: averagePrice,
        formattedFipeValue: formatPrice(priceFipe),
        formattedAutoscarValue: formatPrice(averagePrice),
      },
    ];
    const higherValue = Math.max(averagePrice, priceFipe);
    const userReferenceBar = calculateUserReferenceBar();
    return (
      <div className="w-100 relative">
        <BarChart
          data={data}
          barGap={50}
          width={300}
          height={160}
          margin={{ top: 30, left: 110, right: 10, bottom: 20 }}
        >
          <defs>
            <linearGradient id="firstBarChart" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#fe074e" />
              <stop offset="100%" stopColor="#eb0000" />
            </linearGradient>
          </defs>
          <defs>
            <linearGradient id="secondBarChart" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#ff5e00" stopOpacity={0.6} />
              <stop offset="100%" stopColor="#fe5e00" />
            </linearGradient>
          </defs>
          <XAxis dataKey="name" tickLine={false} />
          <Bar
            dataKey="autoscarValue"
            fill="url(#firstBarChart)"
            radius={[4, 4, 0, 0]}
            maxBarSize={32}
          >
            <LabelList
              dataKey="formattedAutoscarValue"
              position="bottom"
              content={renderLabelValue}
              offset={8}
            />
            <LabelList
              dataKey="firstLabel"
              position="bottom"
              content={renderLabelTitle}
              offset={25}
            />
          </Bar>
          <Bar
            dataKey="fipeValue"
            fill="url(#secondBarChart)"
            radius={[4, 4, 0, 0]}
            barSize={32}
            maxBarSize={32}
          >
            <LabelList
              dataKey="formattedFipeValue"
              position="bottom"
              content={renderLabelValue}
              offset={8}
            />
            <LabelList
              dataKey="secondLabel"
              position="bottom"
              content={renderLabelTitle}
              offset={25}
            />
          </Bar>
          <ReferenceLine
            y={userReferenceBar ? higherValue : data[0].userValue}
            stroke="#54575f"
            strokeDasharray="8 5"
            strokeWidth={2}
            label={handleText}
          />
          <ReferenceLine
            y={userReferenceBar ? higherValue : data[0].userValue}
            stroke="#54575f"
            strokeWidth={0}
            label={handleTitle}
          />
        </BarChart>
      </div>
    );
  };
  const display = ready ? renderChart() : null;
  const classChart = cx('flex items-center', styles.positionChart);
  return (
    <div className="w-100">
      <div className={classChart}>{loading ? renderAnimation() : display}</div>
    </div>
  );
};
